import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, FileText, PlusCircle, BookOpen, Languages, CheckSquare, Archive, Settings, LogOut, Globe, HelpCircle, ShieldCheck } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { cn } from "@/lib/utils";
import { ImportNotifier } from "@/components/ImportNotifier";

const NAV_ITEMS = [
  { key: "dashboard", path: "/dashboard", icon: LayoutDashboard },
  { key: "protocols", path: "/protocoles", icon: FileText },
  { key: "create_protocol", path: "/protocoles/nouveau", icon: PlusCircle },
  { key: "referential", path: "/referentiel", icon: BookOpen },
  { key: "translations", path: "/traductions", icon: Languages },
  { key: "to_validate", path: "/a-valider", icon: CheckSquare },
  { key: "archives", path: "/archives", icon: Archive },
  { key: "admin", path: "/administration", icon: Settings, adminOnly: true },
  { key: "help", path: "/aide", icon: HelpCircle },
  { key: "privacy", path: "/confidentialite", icon: ShieldCheck },
];

export const Layout = () => {
  const { user, logout } = useAuth();
  const { t, lang, toggleLang } = useI18n();
  const navigate = useNavigate();
  const items = NAV_ITEMS.filter((i) => !i.adminOnly || user?.Role === "ADMIN");

  return (
    <div className="min-h-screen bg-[#F8FAFC] dark:bg-[#0B0F17] flex">
      <ImportNotifier />
      <aside className="hidden md:flex w-64 shrink-0 flex-col border-r border-slate-800 bg-slate-900 text-slate-100">
        <div className="h-16 flex items-center gap-2 px-5 border-b border-slate-800">
          <img src="/icons/icon-512.png" alt="" className="w-8 h-8 rounded" />
          <div>
            <p className="font-bold tracking-tight text-sm leading-none">Protocoles Sécurité</p>
            <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">HSE Protocols</p>
          </div>
        </div>
        <nav className="flex-1 py-4 space-y-1 px-3">
          {items.map(({ key, path, icon: Icon }) => (
            <NavLink
              key={key}
              to={path}
              data-testid={`nav-${key.replace(/_/g, "-")}`}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors",
                  isActive ? "bg-amber-500/10 text-amber-400 border-l-2 border-amber-500" : "text-slate-400 hover:bg-slate-800 hover:text-slate-100"
                )
              }
            >
              <Icon className="w-4 h-4" />
              {t(`nav.${key}`)}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-slate-800">
          <button onClick={logout} data-testid="btn-logout-sidebar" className="flex w-full items-center gap-3 px-3 py-2.5 rounded-md text-sm text-slate-400 hover:bg-slate-800 hover:text-rose-400 transition-colors">
            <LogOut className="w-4 h-4" />
            {t("common.logout")}
          </button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 border-b border-slate-200 dark:border-slate-800 bg-white/95 dark:bg-slate-900/95 backdrop-blur-md sticky top-0 z-40 px-4 md:px-6 flex items-center justify-between">
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400 hidden sm:block">{t("tagline")}</p>
          <div className="flex items-center gap-3 ml-auto">
            <button onClick={toggleLang} data-testid="lang-toggle-btn" className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-mono uppercase tracking-wider border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
              <Globe className="w-3.5 h-3.5" />
              {lang}
            </button>
            <button onClick={() => navigate("/administration")} data-testid="user-profile-btn" className="flex items-center gap-2">
              {user?.Picture ? (
                <img src={user.Picture} alt={user.Nom} className="w-8 h-8 rounded-full border border-slate-300 dark:border-slate-700" />
              ) : (
                <div className="w-8 h-8 rounded-full bg-slate-800 text-white flex items-center justify-center text-xs font-bold">{user?.Nom?.[0] || "U"}</div>
              )}
              <div className="hidden lg:block text-left">
                <p className="text-xs font-semibold text-slate-800 dark:text-slate-200 leading-none">{user?.Nom}</p>
                <p className="text-[10px] font-mono uppercase tracking-wide text-slate-500">{t(`roles.${user?.Role}`)}</p>
              </div>
            </button>
          </div>
        </header>
        <main className="flex-1 pb-20 md:pb-0">
          <Outlet />
        </main>
      </div>

      <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-slate-900/95 backdrop-blur-md border-t border-slate-800 flex justify-between px-1 py-1.5">
        {items.slice(0, 5).map(({ key, path, icon: Icon }) => (
          <NavLink
            key={key}
            to={path}
            data-testid={`mobile-nav-${key.replace(/_/g, "-")}`}
            className={({ isActive }) => cn("flex flex-col items-center gap-0.5 px-2 py-1.5 rounded-md text-[10px] font-medium", isActive ? "text-amber-400" : "text-slate-500")}
          >
            <Icon className="w-4 h-4" />
            {t(`nav.${key}`)}
          </NavLink>
        ))}
      </nav>
    </div>
  );
};
